import React, { useState } from 'react';

import { Button, Input, Section } from '../../components';

type ColumnEnumValuesSectionProps = {
  valueStyles?: Record<string, DynamicColumnEnumValueStyle> | null;
  onUpdate: (
    valueStyles: Record<string, DynamicColumnEnumValueStyle> | null
  ) => void;
};

const ColumnEnumValuesSection: React.FC<ColumnEnumValuesSectionProps> = ({
  valueStyles,
  onUpdate,
}) => {
  const [newKey, setNewKey] = useState('');

  const handleAddValue = () => {
    if (!newKey.trim()) return;

    const updated = {
      ...(valueStyles || {}),
      [newKey]: {
        label: newKey,
        color: null,
        backgroundColor: null,
      },
    };
    onUpdate(updated);
    setNewKey('');
  };

  const handleUpdateValue = (
    key: string,
    updates: Partial<DynamicColumnEnumValueStyle>
  ) => {
    const updated = {
      ...valueStyles,
      [key]: { ...valueStyles?.[key], ...updates },
    };
    onUpdate(updated);
  };

  const handleRemoveValue = (key: string) => {
    const updated = { ...valueStyles };
    delete updated[key];
    onUpdate(Object.keys(updated).length > 0 ? updated : null);
  };

  const entries = valueStyles ? Object.entries(valueStyles) : [];

  return (
    <Section
      title="Enum Value Styles"
      subtitle="Configure styling for specific enum values"
    >
      <div className="flex flex-col gap-y-lg">
        {entries.length > 0 && (
          <div className="flex flex-col gap-md">
            {entries.map(([key, style]) => (
              <div
                key={key}
                className="flex flex-col gap-md p-lg bg-content rounded-sm border border-active"
              >
                <div className="flex flex-row items-center justify-between">
                  <span className="text-small font-medium text-text">
                    {key}
                  </span>
                  <Button
                    width="small"
                    type="danger"
                    icon="delete"
                    onClick={() => handleRemoveValue(key)}
                  />
                </div>

                <div className="flex flex-row gap-md">
                  <div className="flex-1">
                    <label className="text-small text-textLight font-normal mb-xs block">
                      Label
                    </label>
                    <Input
                      value={style.label || ''}
                      onChange={val =>
                        handleUpdateValue(key, { label: val || null })
                      }
                      placeholder="Display label"
                      withBorder
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-small text-textLight font-normal mb-xs block">
                      Text Color
                    </label>
                    <Input
                      value={style.color || ''}
                      onChange={val =>
                        handleUpdateValue(key, { color: val || null })
                      }
                      placeholder="#000000"
                      withBorder
                    />
                  </div>
                </div>

                <div className="flex flex-row gap-md">
                  <div className="flex-1">
                    <label className="text-small text-textLight font-normal mb-xs block">
                      Background Color
                    </label>
                    <Input
                      value={style.backgroundColor || ''}
                      onChange={val =>
                        handleUpdateValue(key, {
                          backgroundColor: val || null,
                        })
                      }
                      placeholder="#ffffff"
                      withBorder
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-md p-lg bg-content rounded-sm border border-active">
          <h4 className="text-small font-medium text-text">Add New Value</h4>
          <div className="flex flex-row gap-md">
            <div className="flex-1">
              <Input
                value={newKey}
                onChange={setNewKey}
                placeholder="e.g., active, pending, completed"
                withBorder
              />
            </div>
            <Button width="small" type="primary" onClick={handleAddValue}>
              Add
            </Button>
          </div>
        </div>

        {entries.length === 0 && (
          <div className="text-center text-textLight text-small py-lg">
            No enum values configured yet. Add one above.
          </div>
        )}
      </div>
    </Section>
  );
};

export default ColumnEnumValuesSection;
